import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useEvalCases } from '@/hooks/useEvalCases'
import { useEvalRuns } from '@/hooks/useEvalRuns'
import { useAppAuth } from '@/contexts/app-auth.context'
import { EvalCasesTable } from './EvalCasesTable'
import { EvalRunsTable } from './EvalRunsTable'
import { AddEvalCaseModal } from './AddEvalCaseModal'
import { TriggerRunModal } from './TriggerRunModal'
import { evalApiClient } from '@/lib/api/eval.client'
import type { EvalCase } from '@super/shared-types'

type Section = 'cases' | 'runs'

export function EvalTabContent() {
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const initialTab = searchParams.get('tab') === 'runs' ? 'runs' : 'cases'
  const [section, setSection] = useState<Section>(initialTab)
  const [companyScope, setCompanyScope] = useState<'all' | 'selected'>('all')
  const [addCaseOpen, setAddCaseOpen] = useState(false)
  const [editingCase, setEditingCase] = useState<EvalCase | undefined>(
    undefined,
  )
  const [triggerRunOpen, setTriggerRunOpen] = useState(false)

  // undefined = all cases (for display); specific ID = that company only
  const companyId = companyScope === 'selected' ? currentCompany?.id : undefined

  // Sync tab into URL so back-navigation preserves it
  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (section === 'runs') {
          next.set('tab', 'runs')
        } else {
          next.delete('tab')
        }
        return next
      },
      { replace: true },
    )
  }, [section])

  const {
    cases,
    total: casesTotal,
    limit: casesLimit,
    offset: casesOffset,
    isFetching: casesFetching,
    setOffset: setCasesOffset,
    refetch: refetchCases,
  } = useEvalCases({ companyId })

  const {
    runs,
    total: runsTotal,
    limit: runsLimit,
    offset: runsOffset,
    isFetching: runsFetching,
    setOffset: setRunsOffset,
    triggerRun,
    refetch: refetchRuns,
  } = useEvalRuns({ companyId })

  async function handleDeleteCase(id: string) {
    const ok = await evalApiClient.deleteCase(id)
    if (ok) {
      toast.success('Case deleted')
      refetchCases()
    } else {
      toast.error('Failed to delete case')
    }
  }

  function handleCaseCreated() {
    refetchCases()
    refetchRuns()
  }

  return (
    <div className="flex flex-col gap-4">
      <Tabs value={section} onValueChange={v => setSection(v as Section)}>
        {/* Single toolbar row */}
        <div className="flex items-center justify-between gap-4">
          {/* Left: company scope + divider + section tabs */}
          <div className="flex items-center gap-3">
            {isSuperAdmin && (
              <>
                <Tabs
                  value={companyScope}
                  onValueChange={v => setCompanyScope(v as 'all' | 'selected')}
                >
                  <TabsList mobileDropdown={false}>
                    <TabsTrigger value="all">Global</TabsTrigger>
                    <TabsTrigger value="selected">This Company</TabsTrigger>
                  </TabsList>
                </Tabs>
                <div className="w-px h-6 bg-border" />
              </>
            )}
            <TabsList>
              <TabsTrigger value="cases">Cases</TabsTrigger>
              <TabsTrigger value="runs">Runs</TabsTrigger>
            </TabsList>
          </div>

          {/* Right: context action — fixed position so tabs never jump */}
          <div className="w-32 flex justify-end">
            {section === 'cases' && (
              <Button
                size="sm"
                onClick={() => {
                  setEditingCase(undefined)
                  setAddCaseOpen(true)
                }}
              >
                Add case
              </Button>
            )}
            {section === 'runs' && (
              <Button size="sm" onClick={() => setTriggerRunOpen(true)}>
                Run evaluation
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="cases" className="mt-4">
          <EvalCasesTable
            cases={cases}
            total={casesTotal}
            limit={casesLimit}
            offset={casesOffset}
            onOffsetChange={setCasesOffset}
            onSelect={c => navigate(`/evaluations/cases/${c.id}`)}
            onEdit={c => {
              setEditingCase(c)
              setAddCaseOpen(true)
            }}
            onDelete={handleDeleteCase}
            isLoading={casesFetching}
          />
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <EvalRunsTable
            runs={runs}
            total={runsTotal}
            limit={runsLimit}
            offset={runsOffset}
            onOffsetChange={setRunsOffset}
            onSelectRun={run => navigate(`/evaluations/runs/${run.id}`)}
            isLoading={runsFetching}
          />
        </TabsContent>
      </Tabs>

      <AddEvalCaseModal
        open={addCaseOpen}
        onClose={() => {
          setAddCaseOpen(false)
          setEditingCase(undefined)
        }}
        onCreated={handleCaseCreated}
        initialCase={editingCase}
        companyId={companyId}
        onSuccess={msg => toast.success(msg)}
        onError={msg => toast.error(msg)}
      />

      <TriggerRunModal
        open={triggerRunOpen}
        onClose={() => setTriggerRunOpen(false)}
        onTrigger={triggerRun}
        companyId={companyId}
        companyName={
          companyScope === 'selected'
            ? (currentCompany?.name ?? undefined)
            : undefined
        }
      />
    </div>
  )
}
